import { ReactElement, useCallback, useState } from 'react';

import { useNotification } from 'hooks';
import {
	InboxSignModal,
	ISignedDocInbox,
	useSignedDocsInbox,
} from 'views/signed-doc-inbox';

interface IUseEnvelopeSigning {
	/** The signed-in user's email — the recipient row is matched on it. */
	email?: string;
	/** Refetch the list. Called on BOTH signing exits, see below. */
	reload: () => void;
	/** Marks the row whose signing URL is being fetched; cleared with ''. */
	setBusyRow: (id: string) => void;
	/**
	 * What to do with an envelope this user is not a recipient of. Both decks
	 * open their drawer, but the hook owns no drawer, so the caller decides.
	 */
	onNoRecipient: (envelope: ISignedDocInbox) => void;
}

interface IEnvelopeSigning {
	startSigning: (envelope: ISignedDocInbox) => Promise<void>;
	/** Render this — it is null until a signing URL has been minted. */
	signModal: ReactElement | null;
}

/**
 * Signing an envelope from a deck: mint the URL, show the widget, refresh.
 *
 * Both eSign decks render the same rows off the same `derive.ts`, and each one
 * had a NEED TO SIGN row whose only action opened a drawer — so a recipient
 * could see "Your signature is required on this packet" and had no way to give
 * it. The whole path lives here rather than in either deck so there is one
 * implementation, and so `signModal` cannot be forgotten independently of
 * `startSigning`: a caller that wires up one without the other renders nothing
 * or does nothing, which is visible immediately.
 */
export const useEnvelopeSigning = ({
	email,
	reload,
	setBusyRow,
	onNoRecipient,
}: IUseEnvelopeSigning): IEnvelopeSigning => {
	const { signDocInbox } = useSignedDocsInbox();
	const { successNotification } = useNotification();
	/* The minted signing URL and the environment to embed it in — the only
	   thing that decides whether the widget is on screen. */
	const [signing, setSigning] = useState<{ env: string; url: string } | null>(
		null
	);

	/**
	 * Open the signing widget on an envelope that is waiting on THIS user.
	 *
	 * `/envelope-url` mints a one-off signing URL for one recipient, so the
	 * recipient row has to be resolved first — the same case-insensitive email
	 * match `actionOf` makes when it decides the row says NEED TO SIGN (on a
	 * `signOrder` envelope that is the next unfinished recipient, which by
	 * construction is this user whenever the action is `needToSign`).
	 *
	 * Deliberately NOT gated on a write permission: the one the sent-side deck
	 * holds is `sent`, i.e. the right to act on OTHER people's packets. Signing
	 * is the recipient's own action on their own envelope, and there is no other
	 * route to it from these screens.
	 */
	const startSigning = useCallback(
		async (envelope: ISignedDocInbox) => {
			const mine = (envelope.recipients ?? []).find(
				r => r.email?.toLowerCase() === email?.toLowerCase()
			);
			// No recipient row means there is nobody to sign AS, and the POST could
			// only 400 — show the envelope instead of a failed request.
			if (!mine?._id) {
				onNoRecipient(envelope);
				return;
			}
			setBusyRow(envelope._id);
			const resp = await signDocInbox(
				{ envelopeId: envelope._id, recipientId: mine._id },
				// signDocInbox clears the loader on every path it returns from,
				// the same way resend/download do.
				() => setBusyRow('')
			);
			const { url, env } = resp ?? {};
			// signDocInbox has already surfaced the error notification when it
			// answers with nothing.
			if (url && env) setSigning({ url, env });
		},
		[email, onNoRecipient, setBusyRow, signDocInbox]
	);

	/* A signer can finish, or get part way and close — either way the envelope's
	   state has moved on, so the list is refetched on both paths. */
	const closeSigning = useCallback(() => {
		setSigning(null);
		reload();
	}, [reload]);

	const finishSigning = useCallback(() => {
		setSigning(null);
		setTimeout(() => {
			successNotification('Document signed successfully.');
			reload();
		}, 1000);
	}, [reload, successNotification]);

	return {
		startSigning,
		signModal: signing ? (
			<InboxSignModal
				modalVisibility
				handleCloseIframe={closeSigning}
				prepareUrl={signing}
				handleSubmitModal={finishSigning}
			/>
		) : null,
	};
};
